import * as React from 'react';
import {useAuth0, withAuthenticationRequired} from '@auth0/auth0-react';
import useSessionManager from '../../hooks/useSessionManager';
import MainLoader from '../../../../common/components/main-loader/MainLoader';
import NoAccessScreen from '../../../../common/components/no-access-screen/NoAccessScreen';
import Root from '../../../../common/router/view/Root';

/**
 * Bridges the Auth0 React context with the Injex `SessionManager`.
 *
 * Once Auth0 reports an authenticated user, it wires the token provider and
 * logout handler into the SessionManager and bootstraps the backend session
 * (`/api/accounts/me`). The app router (`Root`) is rendered only after the
 * session has been initialized.
 */
function AuthGate(): React.JSX.Element {
    const {isLoading, isAuthenticated, getAccessTokenSilently, user, logout, loginWithRedirect} = useAuth0();
    const sessionManager = useSessionManager();
    const [isSessionReady, setIsSessionReady] = React.useState(false);

    React.useEffect(() => {
        sessionManager.setTokenProvider(() => getAccessTokenSilently());
        sessionManager.authLogout = () => logout({logoutParams: {returnTo: window.location.origin}});
    }, [getAccessTokenSilently, logout, sessionManager]);

    React.useEffect(() => {
        let cancelled = false;

        async function initializeSession() {
            if (!isAuthenticated || !user) {
                return;
            }

            try {
                const token = await getAccessTokenSilently();
                if (cancelled) {
                    return;
                }
                await sessionManager.onSessionChange(token, user);
                if (!cancelled) {
                    setIsSessionReady(true);
                }
            } catch (err) {
                if (cancelled) {
                    return;
                }
                // Auth0 can still report isAuthenticated from a stale cache while
                // the refresh token is dead — force a fresh login instead of NoAccess.
                console.error('Failed to initialize session', err);
                loginWithRedirect();
            }
        }

        initializeSession();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, user, getAccessTokenSilently, sessionManager, loginWithRedirect]);

    if (isLoading || !isAuthenticated || !isSessionReady) {
        return <MainLoader/>;
    }

    if (!sessionManager.hasAppAccess) {
        return <NoAccessScreen/>;
    }

    return <Root/>;
}

const AuthenticatedGate = withAuthenticationRequired(AuthGate, {
    onRedirecting: () => <MainLoader/>
});

export default function AuthGateWithErrorHandling(): React.JSX.Element {
    const {error, loginWithRedirect} = useAuth0();
    const [retryError, setRetryError] = React.useState('');
    const [isRetrying, setIsRetrying] = React.useState(false);

    const retryLogin = async () => {
        setIsRetrying(true);
        setRetryError('');
        try {
            await loginWithRedirect({authorizationParams: {prompt: 'login'}});
        } catch (err) {
            setRetryError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
        } finally {
            setIsRetrying(false);
        }
    };

    // Check callback errors before mounting the wrapper that automatically redirects.
    if (error) {
        return (
            <div role="alert" style={{padding: 40, textAlign: 'center'}}>
                <h2>Authentication error</h2>
                <p>{retryError || error.message}</p>
                <button type="button" disabled={isRetrying} onClick={retryLogin}>Sign in again</button>
            </div>
        );
    }

    return <AuthenticatedGate/>;
}
